import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, LineChart, Line, Legend
} from 'recharts';
import { TrendingUp, TrendingDown, Users, DollarSign, Calendar, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PERIODS = [
  { value: 'day', label: 'Jours' },
  { value: 'week', label: 'Semaines' },
  { value: 'month', label: 'Mois' },
  { value: 'year', label: 'Années' }
];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-100">
        <p className="font-semibold text-gray-900 mb-2">{label}</p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-2 text-sm">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-600">{entry.name}:</span>
            <span className="font-medium text-gray-900">
              {entry.name === 'Revenus' ? `${entry.value} CHF` : entry.value}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const TrendBadge = ({ value, label }) => {
  const isPositive = value >= 0;
  return (
    <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
      isPositive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
    }`}>
      {isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      <span>{isPositive ? '+' : ''}{value}%</span>
      {label && <span className="text-gray-500 ml-1">{label}</span>}
    </div>
  );
};

const AnalyticsCharts = () => {
  const [period, setPeriod] = useState('week');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeChart, setActiveChart] = useState('revenue');

  useEffect(() => {
    loadAnalytics();
  }, [period]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/analytics/dashboard?period=${period}`, {
        withCredentials: true
      });
      setData(response.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-8 text-gray-500">
        Impossible de charger les analytics
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-gray-900 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-purple-500" />
          Évolution
        </h4>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                period === p.value
                  ? 'bg-white text-purple-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards with Trends */}
      <div className="grid grid-cols-3 gap-4">
        <div 
          className={`bg-white rounded-xl p-4 cursor-pointer transition-all border-2 ${
            activeChart === 'revenue' ? 'border-green-500 shadow-lg' : 'border-transparent hover:border-gray-200'
          }`}
          onClick={() => setActiveChart('revenue')}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
              <DollarSign className="h-5 w-5 text-white" />
            </div>
            {data.trends?.revenue !== 0 && (
              <TrendBadge value={data.trends.revenue} />
            )}
          </div>
          <p className="text-2xl font-bold text-gray-900">{data.totals?.revenue?.toFixed(2) || 0} CHF</p>
          <p className="text-sm text-gray-500">Revenus</p>
        </div>

        <div 
          className={`bg-white rounded-xl p-4 cursor-pointer transition-all border-2 ${
            activeChart === 'followers' ? 'border-purple-500 shadow-lg' : 'border-transparent hover:border-gray-200'
          }`}
          onClick={() => setActiveChart('followers')}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
              <Users className="h-5 w-5 text-white" />
            </div>
            {data.trends?.followers !== 0 && (
              <TrendBadge value={data.trends.followers} />
            )}
          </div>
          <p className="text-2xl font-bold text-gray-900">{data.totals?.followers || 0}</p>
          <p className="text-sm text-gray-500">Abonnés</p>
        </div>

        <div 
          className={`bg-white rounded-xl p-4 cursor-pointer transition-all border-2 ${
            activeChart === 'sessions' ? 'border-blue-500 shadow-lg' : 'border-transparent hover:border-gray-200'
          }`}
          onClick={() => setActiveChart('sessions')}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
              <Calendar className="h-5 w-5 text-white" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">{data.totals?.sessions || 0}</p>
          <p className="text-sm text-gray-500">Sessions</p>
        </div>
      </div>

      {/* Main Chart */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h5 className="font-medium text-gray-700">
            {activeChart === 'revenue' && 'Évolution des revenus'}
            {activeChart === 'followers' && 'Évolution des abonnés'}
            {activeChart === 'sessions' && 'Sessions réalisées'}
          </h5>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant={activeChart === 'revenue' ? 'default' : 'outline'}
              onClick={() => setActiveChart('revenue')}
              className={activeChart === 'revenue' ? 'bg-green-500 hover:bg-green-600' : ''}
            >
              Revenus
            </Button>
            <Button
              size="sm"
              variant={activeChart === 'followers' ? 'default' : 'outline'}
              onClick={() => setActiveChart('followers')}
              className={activeChart === 'followers' ? 'bg-purple-500 hover:bg-purple-600' : ''}
            >
              Abonnés
            </Button>
            <Button
              size="sm"
              variant={activeChart === 'sessions' ? 'default' : 'outline'}
              onClick={() => setActiveChart('sessions')}
              className={activeChart === 'sessions' ? 'bg-blue-500 hover:bg-blue-600' : ''}
            >
              Sessions
            </Button>
          </div>
        </div>

        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            {activeChart === 'revenue' ? (
              <AreaChart data={data.chart_data}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="name" 
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={{ stroke: '#e5e7eb' }}
                />
                <YAxis 
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={{ stroke: '#e5e7eb' }}
                  tickFormatter={(value) => `${value} CHF`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area 
                  type="monotone" 
                  dataKey="revenue" 
                  name="Revenus"
                  stroke="#10b981" 
                  strokeWidth={2}
                  fillOpacity={1} 
                  fill="url(#colorRevenue)" 
                />
              </AreaChart>
            ) : activeChart === 'followers' ? (
              <LineChart data={data.chart_data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="name" 
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={{ stroke: '#e5e7eb' }}
                />
                <YAxis 
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={{ stroke: '#e5e7eb' }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Line 
                  type="monotone" 
                  dataKey="followers" 
                  name="Nouveaux abonnés"
                  stroke="#8b5cf6" 
                  strokeWidth={3}
                  dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: '#8b5cf6', strokeWidth: 2 }}
                />
              </LineChart>
            ) : (
              <BarChart data={data.chart_data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="name" 
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={{ stroke: '#e5e7eb' }}
                />
                <YAxis 
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={{ stroke: '#e5e7eb' }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey="sessions" 
                  name="Sessions"
                  fill="#3b82f6" 
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>

        {/* Chart Legend */}
        <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span>Revenus (CHF)</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <div className="w-3 h-3 rounded-full bg-purple-500" />
            <span>Abonnés</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <div className="w-3 h-3 rounded-full bg-blue-500" />
            <span>Sessions</span>
          </div>
        </div>
      </div>

      {/* Combined Overview Chart */}
      <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border border-gray-100">
        <h5 className="font-medium text-gray-700 mb-4">Vue d'ensemble combinée</h5>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.chart_data} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis 
                dataKey="name" 
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis 
                yAxisId="left"
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis 
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar yAxisId="left" dataKey="followers" name="Abonnés" fill="#8b5cf6" radius={[2, 2, 0, 0]} />
              <Bar yAxisId="left" dataKey="sessions" name="Sessions" fill="#3b82f6" radius={[2, 2, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="revenue" name="Revenus" stroke="#10b981" strokeWidth={2} dot={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsCharts;
